<template>
  <swiper
    ref="kr-carousel"
    class="my-[10px] pb-[10px]"
    :class="{
      'overflow-auto': isMobileSafari,
      'overflow-hidden': !isMobileSafari,
    }"
    :options="{ ...swiperOptions, allowTouchMove }"
    @slideChange="handleSlideChange"
  >
    <!-- slides -->
    <swiper-slide
      v-for="(item, index) in message.data.content"
      :key="index"
      class="max-w-[65%] h-auto"
    >
      <el-card
        class="message-card-item h-fit"
        :body-style="{
          padding: '0',
          height: 'fit-content',
          display: itemImage(item) && item.text ? 'flex' : 'block',
          flexDirection: 'column',
        }"
      >
        <div v-if="itemImage(item) && item.text" class="bg-white">
          <img
            v-if="itemImage(item)"
            :src="itemImage(item)"
            :class="[
              'h-[160px] w-full',
              !settings.disableLightBoxImage ? 'cursor-pointer' : '',
              imageObjectClass(item),
            ]"
            @click="setLightBoxImage(item)"
          />
        </div>

        <div v-else-if="itemImage(item)">
          <a
            :class="!settings.disableLightBoxImage ? 'cursor-pointer' : ''"
            @click="setLightBoxImage(item)"
          >
            <img class="w-full" :src="itemImage(item)" />
          </a>
        </div>

        <div class="message-card-content flex !flex-col h-full">
          <div v-if="item.text || item.subtext" class="p-2">
            <div class="leading-[18px]">
              <span
                v-text="item.text"
                class="message-card-title carousel-font !font-semibold !text-base"
              />
            </div>
            <div class="my-1 leading-4">
              <div
                v-if="
                  !shouldShowFullContent[index] &&
                  readMoreEnabled &&
                  item.subtext?.length > readMoreCharacterCount
                "
              >
                <small class="subtext carousel-font !text-sm !text-[#333]">
                  {{ truncateText(item.subtext) }}
                </small>
                <div class="keyreply-read-more" @click="setShowFullContent(index)">Read More</div>
              </div>
              <div v-else>
                <small class="subtext carousel-font text-sm text-[#333]">
                  {{ item.subtext }}
                </small>
              </div>
            </div>
          </div>

          <el-row type="text" class="!flex-col !px-[10px]">
            <div v-for="(button, buttonIndex) in item.buttons" :key="`button-${buttonIndex}`">
              <el-button
                v-if="
                  !((button.type && button.type === 'website') || button.url) ||
                  isDisabledButton(button) ||
                  button.popupWindow?.enabled
                "
                class="message-card-button carousel-font !w-full !mb-[10px] !whitespace-normal"
                :class="{ 'blue-button': buttonOutline === 'primary' }"
                v-text="button.text"
                :size="isUiBetaEnabled ? 'medium' : 'small'"
                target="_blank"
                :plain="true"
                :type="buttonOutline"
                :disabled="isDisabledButton(button)"
                @click.stop.prevent="handleButtonClick($event, button)"
              />
              <a
                v-else
                @click="$emit('sendPostback', button)"
                :href="validUrl(button.url)"
                :target="button.popup ?? '_blank'"
              >
                <el-button
                  class="message-card-button carousel-font w-full mb-[10px] whitespace-normal"
                  :class="{ 'blue-button': buttonOutline === 'primary' }"
                  v-text="button.text"
                  :size="isUiBetaEnabled ? 'medium' : 'small'"
                  target="_blank"
                  :plain="true"
                  :type="buttonOutline"
                />
              </a>
            </div>
          </el-row>
        </div>
        <div
          class="navigation carousel-font"
          v-if="totalSlides > 1 && isUiBetaEnabled"
          :class="{ disabled: current !== index }"
        >
          <button v-if="current > 0" class="navigation-prev" @click="prev">
            <i :class="navigationIconClasses.prev"></i>
          </button>
          <button class="navigation-next" @click="next" v-if="current < totalSlides - 1">
            <i :class="navigationIconClasses.next"></i>
          </button>
        </div>
      </el-card>
    </swiper-slide>

    <button
      v-if="message.data.content.length > 1 && !isUiBetaEnabled"
      class="el-carousel__arrow el-carousel__arrow--left carousel-font carousel-arrow"
      slot="button-prev"
      @click="prev"
    >
      <i class="el-icon-arrow-left" />
    </button>
    <button
      v-if="message.data.content.length > 1 && !isUiBetaEnabled"
      class="el-carousel__arrow el-carousel__arrow--right carousel-font carousel-arrow"
      slot="button-next"
      @click="next"
    >
      <i class="el-icon-arrow-right" />
    </button>

    <div class="pagination" slot="pagination" v-if="totalSlides > 1 && isUiBetaEnabled">
      <span
        class="dot"
        v-for="(item, index) in message.data.content"
        :key="index"
        :class="{ active: current === index }"
        @click="handleNavigateSlide(index)"
      />
    </div>
  </swiper>
</template>

<script>
import Vue from "vue";
import {
  Swiper as SwiperClass,
  // Pagination,
  // Mousewheel,
  // Autoplay,
} from "swiper/core";
import getAwesomeSwiper from "vue-awesome-swiper/dist/exporter";
import _ from "lodash-es";
import isDisabledButton from "@/helpers/isDisabledButton";
import { mapGetters } from "vuex";
import validUrl from "@/helpers/validUrl";

const { Swiper, SwiperSlide } = getAwesomeSwiper(SwiperClass);

export default {
  props: [
    "message",
    "buttonOutline",
    "shouldDisableIfInLivechat",
    "backgroundStyle",
    "titleStyle",
    "iconStyle",
  ],
  components: {
    Swiper,
    SwiperSlide,
  },
  data() {
    return {
      current: 0,
      swiperOptions: {
        slidesPerView: "auto",
        centeredSlides: true,
        spaceBetween: 20,
        pagination: {
          el: ".pagination",
          clickable: true,
        },
        navigation: {
          nextEl: ".el-carousel__arrow--right",
          prevEl: ".el-carousel__arrow--left",
        },
        direction: "horizontal",
      },
      contentUrls: [],
      shouldShowFullContent: {},
    };
  },

  computed: {
    ...mapGetters(["isRtlModeEnabled", "isUiBetaEnabled"]),
    /**
     * @description Most of the webchat config
     * @return {any}
     */
    settings() {
      return this.$store.getters.settings;
    },
    isMobileSafari() {
      let ua = window.navigator.userAgent;
      return /(iPad|iPhone|iPod).*WebKit/.test(ua) && !/(CriOS|OPiOS)/.test(ua);
    },
    swiper() {
      return this.$refs["kr-carousel"].$swiper;
    },
    totalSlides() {
      return this.message.data.content.length;
    },
    allowTouchMove() {
      return this.totalSlides > 1;
    },
    navigationIconClasses() {
      return {
        next: this.isRtlModeEnabled ? "el-icon-arrow-left" : "el-icon-arrow-right",
        prev: this.isRtlModeEnabled ? "el-icon-arrow-right" : "el-icon-arrow-left",
      };
    },
    readMoreCharacterCount() {
      return this.settings.readMoreConfigure.readMoreCharacterCount;
    },
    readMoreEnabled() {
      return this.settings.readMoreConfigure.enabled;
    },
  },
  methods: {
    validUrl,
    setLightBoxImage(item) {
      if (this.settings.disableLightBoxImage) {
        return;
      }
      this.$emit("setLightBoxImage", this.itemImage(item));
    },
    itemImage(item) {
      const image = item.image || _.get(item, "images[0]");
      return image;
    },

    /**
     * @description Go to next slide
     * @return {void}
     */
    next() {
      this.swiper.slideNext();
    },

    /**
     * @description Go to previous slide
     * @return {void}
     */
    prev() {
      this.swiper.slidePrev();
    },

    isDisabledButton(button) {
      return isDisabledButton(this, button);
    },

    handleButtonClick($event, button) {
      // Handle popup window with custom dimensions
      if (button.url && button.popupWindow?.enabled) {
        $event.preventDefault();
        const width = button.popupWindow.width || 500;
        const height = button.popupWindow.height || 600;
        const windowFeatures = `width=${width},height=${height}`;
        window.open(this.validUrl(button.url), "_blank", windowFeatures);
      }
      this.$emit("sendPostback", button);
    },

    handleNavigateSlide(index) {
      this.swiper.slideTo(index);
    },

    handleSlideChange() {
      this.current = this.swiper.activeIndex;
    },

    truncateText(text) {
      if (!text) return "";
      return text.slice(0, this.readMoreCharacterCount) + "...";
    },
    setShowFullContent(index) {
      this.$set(this.shouldShowFullContent, index, true);
    },
    imageObjectClass(item) {
      return !_.get(item.options, "padded") ? "object-cover" : "object-contain";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~swiper/swiper-bundle.css";
>>> .swiper-wrapper {
  height: auto;
  align-items: stretch;
}
</style>

<style lang="scss" scoped>
.carousel-font {
  font-family: var(--keyreply-font-family);
}

.carousel-arrow {
  background: rgba(31, 45, 61, 0.5) !important;
  min-width: auto !important;
}

.keyreply-read-more {
  color: var(--keyreply-agent-text-color);
  font-weight: 600;
  font-style: italic;
  cursor: pointer;
}

.message-card-item {
  background: var(--keyreply-user-main-bg-color);
}

.ui-beta .message-card-title {
  color: var(--keyreply-user-main-title-color);
}

.ui-beta {
  .swiper-container {
    overflow: visible !important;
  }

  .swiper-slide {
    max-width: 75% !important;
  }

  .message-card-item {
    border-radius: 16px;
    overflow: hidden;
    // height: 100% !important;
  }

  .message-card-content {
    text-align: left;
  }

  .message-card-button {
    display: inline-block;
    border-radius: 8px;
    font-size: 12px;

    &.blue-button {
      background: #fff;
      border: 1px solid #4e6cce;
      color: #4e6cce;

      &:hover,
      &:focus,
      &:active {
        background: #4e6cce !important;
        color: #fff;
      }
    }
  }

  .message-card-title {
    display: inline-block;
    font-size: 20px;
    margin-bottom: 16px;
    text-align: left;
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 16px;
    width: 100%;

    .dot {
      width: 4px;
      height: 4px;
      background: #999;
      border-radius: 200px;
      cursor: pointer;
      transition: all 0.5s ease-in-out;

      &.active {
        background: #333;
      }
    }
  }

  .navigation {
    display: flex;
    justify-content: space-between;
    box-sizing: border-box;
    padding: 8px;

    &.disabled {
      opacity: 0;
    }

    &-next,
    &-prev {
      -webkit-text-size-adjust: none !important;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 27px !important;
      height: 27px !important;
      min-width: 27px !important;
      min-height: 27px !important;
      border-radius: 50%;
      border: none;
      outline: none;
      cursor: pointer;
      background: var(--keyreply-widget-icon-bg-color);
      color: var(--keyreply-widget-icon-text-color);

      i {
        font-size: 16px;
      }
    }
    &-next {
      i {
        margin-left: 2px;
      }
    }
  }
}

[dir="ltr"] .message-card-content {
  text-align: left;
  padding: 10px;
}

[dir="rtl"] .message-card-content {
  text-align: right;
}

[dir="ltr"] .message-card-button {
  margin-right: 8px !important;
}

[dir="rtl"] .message-card-button {
  margin-left: 8px !important;
}

[dir="ltr"] .ui-beta .pagination {
  .dot {
    margin-right: 3px;
  }
}

[dir="rtl"] .ui-beta .pagination {
  .dot {
    margin-left: 3px;
  }
}

[dir="ltr"] .ui-beta .navigation {
  &-next {
    margin-left: auto;
  }
}

[dir="rtl"] .ui-beta .navigation {
  &-next {
    margin-right: auto;
  }
}
</style>

<style lang="scss">
.ui-beta {
  .message-card-item .el-card__body {
    height: 100% !important;
    display: flex !important;
    justify-content: space-between !important;
  }
  .swiper-wrapper {
    height: auto !important;
  }
}
</style>
